import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Bookmark, Trash2, ToggleLeft, ToggleRight, Sparkles, AlertTriangle } from 'lucide-react';
import { getTransactionRules, updateTransactionRule, deleteTransactionRule, applyTransactionRules } from '@/lib/bankTransactionsApi';
import { toast } from 'sonner';

export default function RulesDialog({ open, onOpenChange, onApplied }) {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const fetchRules = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getTransactionRules();
      setRules(data);
    } catch { toast.error('Failed to load rules'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open) fetchRules(); }, [open, fetchRules]);

  const handleToggle = async (rule) => {
    try {
      const updated = await updateTransactionRule(rule.id, { active: !rule.active });
      setRules(rs => rs.map(r => r.id === rule.id ? updated : r));
    } catch { toast.error('Failed to toggle rule'); }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await deleteTransactionRule(deleteConfirm.id);
      setRules(rs => rs.filter(r => r.id !== deleteConfirm.id));
      setDeleteConfirm(null);
      toast.success('Rule deleted');
    } catch { toast.error('Failed to delete rule'); }
  };

  const handleApply = async () => {
    setApplying(true);
    try {
      const res = await applyTransactionRules();
      toast.success(`Applied rules to ${res.applied} of ${res.checked} uncategorized transactions`);
      fetchRules();
      if (onApplied) onApplied();
    } catch { toast.error('Apply failed'); }
    finally { setApplying(false); }
  };

  const catLabel = (r) => [r.category, r.subcategory, r.detail].filter(Boolean).join(' > ');

  const activeCount = rules.filter(r => r.active).length;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="rules-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bookmark className="h-4 w-4 text-primary" />
              Categorization Rules
            </DialogTitle>
            <DialogDescription>
              Rules match transaction descriptions and auto-apply categories — on import and on demand.
              {rules.length > 0 && <span className="ml-2">{activeCount} active · {rules.length - activeCount} disabled</span>}
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-center gap-2 pb-2 border-b border-border/40">
            <Button
              size="sm"
              onClick={handleApply}
              disabled={applying || activeCount === 0}
              data-testid="apply-rules-btn"
            >
              <Sparkles className="h-3.5 w-3.5 mr-1" />
              {applying ? 'Applying...' : 'Apply Rules to Uncategorized'}
            </Button>
            <span className="text-[11px] text-muted-foreground">
              Matches active rules against uncategorized transactions
            </span>
          </div>

          <div className="flex-1 overflow-y-auto -mx-6 px-6 pt-2">
            {loading ? (
              <div className="py-12 text-center text-sm text-muted-foreground">Loading...</div>
            ) : rules.length === 0 ? (
              <div className="py-12 text-center text-sm text-muted-foreground" data-testid="rules-empty-state">
                <Bookmark className="h-8 w-8 mx-auto mb-2 opacity-40" />
                <p className="font-medium">No rules yet</p>
                <p className="text-xs mt-1">
                  Rules are created automatically when you check "Remember this" in the AI Review dialog.
                </p>
              </div>
            ) : (
              <div className="rounded-lg border border-border/60 bg-white overflow-hidden">
                <table className="w-full text-xs" data-testid="rules-table">
                  <thead>
                    <tr className="bg-[#f5f0eb] text-left text-[10px] font-semibold uppercase tracking-wide text-muted-foreground border-b border-border/50">
                      <th className="px-3 py-2 w-12">Active</th>
                      <th className="px-3 py-2">Pattern</th>
                      <th className="px-3 py-2">→ Category</th>
                      <th className="px-3 py-2 text-right w-16">Hits</th>
                      <th className="px-3 py-2 text-right w-16">Del</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rules.map((r, idx) => (
                      <tr key={r.id} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-[#fafaf7]'} border-b border-border/30 last:border-b-0 ${r.active ? '' : 'opacity-60'}`} data-testid="rule-row">
                        <td className="px-3 py-2">
                          <button onClick={() => handleToggle(r)} title={r.active ? 'Disable' : 'Enable'} data-testid="toggle-rule-btn">
                            {r.active ? (
                              <ToggleRight className="h-5 w-5 text-primary" />
                            ) : (
                              <ToggleLeft className="h-5 w-5 text-muted-foreground" />
                            )}
                          </button>
                        </td>
                        <td className="px-3 py-2 max-w-[280px]">
                          <div className="font-mono text-[11px] text-[#1f2933] truncate" title={r.pattern} data-testid="rule-pattern">{r.pattern}</div>
                          {r.sample_description && (
                            <div className="text-[10px] text-muted-foreground truncate mt-0.5" title={r.sample_description}>
                              e.g. {r.sample_description}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <Badge variant="secondary" className="text-[10px]">{catLabel(r)}</Badge>
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-[#5b6773]" data-testid="rule-hit-count">{r.hit_count}</td>
                        <td className="px-3 py-2 text-right">
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground hover:text-red-600" onClick={() => setDeleteConfirm(r)} data-testid="delete-rule-btn">
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteConfirm} onOpenChange={v => { if (!v) setDeleteConfirm(null); }}>
        <DialogContent className="max-w-sm" data-testid="delete-rule-confirm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-500" />
              Delete rule?
            </DialogTitle>
            <DialogDescription>
              The rule will no longer auto-categorize matching transactions. Existing categorized transactions are not affected.
            </DialogDescription>
          </DialogHeader>
          {deleteConfirm && (
            <div className="rounded-lg border border-border/60 bg-[#faf8f4] p-3 text-xs space-y-1">
              <p className="font-mono text-[11px]">{deleteConfirm.pattern}</p>
              <Badge variant="secondary" className="text-[10px]">{catLabel(deleteConfirm)}</Badge>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-rule-btn">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
